import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import { useReactToPrint } from 'react-to-print';
import dayjs from 'dayjs';

interface PrintRef {
  print: () => void;
}

interface InvoicePrintComponentProps {
  record: any;
  patient?: any;
}

export const InvoicePrintComponent = forwardRef<
  PrintRef,
  InvoicePrintComponentProps
>(({ record, patient }, ref) => {
  const componentRef = useRef<HTMLDivElement>(null);
  const data = record;
  const patientDetails = patient;
  const CURRENCY_SYMBOL = '₹';

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Invoice-${data.patient_id || 'New'}`,
    onBeforePrint: async () => {
      // Add print-specific styles
      const style = document.createElement('style');
      style.innerHTML = `
          @media print {
            body * {
              visibility: hidden;
            }
            #invoice-print, #invoice-print * {
              visibility: visible;
            }
            #invoice-print {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
          }
        `;
      document.head.appendChild(style);
    },
    onAfterPrint: () => {
      // Clean up print-specific styles
      const style = document.querySelector('style[data-print-style]');
      if (style) {
        style.remove();
      }
    },
  });

  useImperativeHandle(ref, () => ({
    print: handlePrint,
  }));

  // Helper function to safely format currency
  const formatCurrency = (value: number | undefined | null) => {
    if (value === undefined || value === null) return '0';
    return parseFloat(value.toString()).toFixed(2);
  };

  // Helper function to safely format date
  const formatDate = (date: string | undefined | null) => {
    if (!date) return '';
    return dayjs(date).format('DD/MM/YYYY');
  };

  // Helper function to calculate age
  const calculateAge = (birthDate: string | null) => {
    if (!birthDate) return 'N/A';
    return dayjs().diff(dayjs(birthDate), 'year');
  };

  // Get user name helper (you might need to adjust this based on your data structure)
  const getUserName = (userId: number | null) => {
    if (!userId) return 'N/A';
    // You can implement logic to get user name based on ID
    // For now, using the existing data from the record
    if (userId === data.doctor_id && data.doctor) {
      return `${data.doctor.first_name || ''} ${data.doctor.last_name || ''}`;
    }
    if (userId === data.counsellor_id && data.counsellor) {
      return `${data.counsellor.first_name || ''} ${data.counsellor.last_name || ''}`;
    }
    if (userId === data.therapist_id && data.therapist) {
      return `${data.therapist.first_name || ''} ${data.therapist.last_name || ''}`;
    }
    return 'N/A';
  };

  // Calculate subtotal (sum of unit_price * quantity for all items)
  const subTotal =
    data.clinic_invoice_items?.reduce((sum: number, item: any) => {
      return (
        sum +
        (parseFloat(item.unit_price) * parseFloat(item.quantity || 1) || 0)
      );
    }, 0) || 0;

  // Calculate total item discounts (sum of discount amounts for all items)
  const totalItemDiscount =
    data.clinic_invoice_items?.reduce((sum: number, item: any) => {
      const itemTotal =
        parseFloat(item.unit_price) * parseFloat(item.quantity || 1);
      const discountAmount = (itemTotal * parseFloat(item.discount || 0)) / 100;
      return sum + discountAmount;
    }, 0) || 0;

  // Total discount = item discounts + overall discount
  const totalDiscount =
    totalItemDiscount + parseFloat(data.overall_discount_amount || 0);

  // Calculate subtotal after item discounts (before overall discount and GST)
  const subtotalAfterItemDiscounts = subTotal - totalItemDiscount;

  return (
    <div className="hidden print:block">
      <div
        ref={componentRef}
        id="invoice-print"
        className="bg-white min-h-screen p-8"
        style={{
          paddingLeft: '3rem',
          paddingRight: '3rem',
          fontFamily: 'Arial, sans-serif',
        }}
      >
        <div className="max-w-5xl mx-auto pt-[8rem] pl-10 pr-4 pb-4">
          {/* Invoice Details Section */}
          <div className="grid grid-cols-2 gap-8">
            {/* Patient Information */}
            <div
              className="py-2 px-2 rounded-lg"
              style={{ border: '1px solid #e5e7eb' }}
            >
              <h4
                className="text-xs font-semibold text-gray-900 pb-1"
                style={{ borderBottom: '1px solid #d1d5db' }}
              >
                Patient Information
              </h4>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 text-xs">
                    Name:
                  </span>
                  <span className="text-gray-900 font-semibold text-xs">
                    {patientDetails
                      ? `${patientDetails.first_name} ${patientDetails.last_name || ''}`
                      : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 text-xs">
                    Patient ID:
                  </span>
                  <span className="text-gray-900 font-semibold text-xs">
                    {data.patient_id}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 text-xs">
                    Doctor:
                  </span>
                  <span className="text-gray-900 text-xs">
                    {getUserName(data.doctor_id)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 text-xs">
                    Counsellor:
                  </span>
                  <span className="text-gray-900 text-xs">
                    {getUserName(data.counsellor_id)}
                  </span>
                </div>
              </div>
            </div>

            {/* Invoice Details */}
            <div
              className="py-2 px-2 rounded-lg"
              style={{ border: '1px solid #e5e7eb' }}
            >
              <h4
                className="text-xs font-semibold text-gray-900 pb-1"
                style={{ borderBottom: '1px solid #d1d5db' }}
              >
                Invoice Details
              </h4>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 text-xs">
                    Invoice Date:
                  </span>
                  <span className="text-gray-900 font-semibold text-xs">
                    {formatDate(data.created_at)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 text-xs">
                    Invoice #:
                  </span>
                  <span className="text-gray-900 font-semibold text-xs">
                    INV-{data.patient_id || 'NEW'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 text-xs">
                    Status:
                  </span>
                  <span className="text-gray-800 text-xs font-medium">
                    {data.invoice_status || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Services Table */}
          <div className="mb-4 pt-2">
            <h4 className="text-xs font-semibold text-gray-900 ">
              Services & Charges
            </h4>
            <div
              className="rounded-lg overflow-hidden"
              style={{ border: '1px solid #e5e7eb' }}
            >
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <th className="px-4 py-2 text-left font-semibold text-xs text-gray-900">
                      Service/Package Name
                    </th>
                    <th className="px-3 py-2 text-center font-semibold text-xs text-gray-900">
                      Quantity
                    </th>
                    <th className="px-3 py-2 text-right font-semibold text-xs text-gray-900">
                      Unit Price
                    </th>
                    <th className="px-3 py-2 text-center font-semibold text-xs text-gray-900">
                      Discount
                    </th>
                    <th className="px-3 py-2 text-center font-semibold text-xs text-gray-900">
                      GST
                    </th>
                    <th className="px-4 py-2 text-right font-semibold text-xs text-gray-900">
                      Total Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.clinic_invoice_items?.map(
                    (item: any, index: number) => (
                      <tr
                        key={index}
                        style={{ borderTop: '1px solid #e5e7eb' }}
                      >
                        <td className="px-4 py-1.5 text-gray-900 font-medium text-xs">
                          {item.item_name || 'N/A'}
                        </td>
                        <td className="px-3 py-1.5 text-center text-gray-700 text-xs">
                          {item.quantity || 1}
                        </td>
                        <td className="px-3 py-1.5 text-right text-gray-700 font-medium text-xs">
                          {CURRENCY_SYMBOL}
                          {formatCurrency(item.unit_price)}
                        </td>
                        <td className="px-3 py-1.5 text-center text-gray-700 text-xs">
                          {item.discount || 0}%
                        </td>
                        <td className="px-3 py-1.5 text-center text-gray-700 text-xs">
                          {item.gst_rate || 0}%
                        </td>
                        <td className="px-4 py-1.5 text-right text-gray-900 font-semibold text-xs">
                          {CURRENCY_SYMBOL}
                          {formatCurrency(item.final_price)}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '1px solid #d1d5db' }}>
                    <td className="px-4 py-2 text-right font-bold text-xs text-gray-900"></td>
                    <td className="px-3 py-2 text-center font-bold text-xs text-gray-900"></td>
                    <td className="px-3 py-2 text-right font-bold text-xs text-gray-900">
                      {CURRENCY_SYMBOL}
                      {formatCurrency(subTotal)}
                    </td>
                    <td className="px-3 py-2 text-center font-bold text-xs text-gray-900">
                      {formatCurrency(totalItemDiscount)}
                    </td>
                    <td className="px-3 py-2 text-center font-bold text-xs text-gray-900">
                      {formatCurrency(data.gst)}
                    </td>
                    <td className="px-4 py-2 text-right font-bold text-xs text-gray-900">
                      {CURRENCY_SYMBOL}
                      {formatCurrency(data.total_amount)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Summary and Remarks */}
          <div className="grid grid-cols-2 gap-8">
            {/* Remarks Section */}
            <div
              className="py-2 px-2 rounded-lg"
              style={{ border: '1px solid #e5e7eb' }}
            >
              <h4
                className="text-xs font-semibold text-gray-900 pb-1"
                style={{ borderBottom: '1px solid #d1d5db' }}
              >
                Remarks
              </h4>
              <div className="min-h-[90px] text-gray-700 text-xs">
                {data.remark ? (
                  <div>
                    {(() => {
                      // Handle multiple levels of escaping by recursively replacing \\n with \n
                      let processedText = data.remark;
                      while (processedText.includes('\\n')) {
                        processedText = processedText.replace(/\\n/g, '\n');
                      }
                      return processedText
                        .split('\n')
                        .map((line: string, index: number) => {
                          const trimmedLine = line.trim();
                          if (!trimmedLine) return null;
                          return (
                            <div key={index} className="flex items-start gap-2">
                              <span>•</span>
                              <span>{trimmedLine}</span>
                            </div>
                          );
                        });
                    })()}
                  </div>
                ) : (
                  <span className="text-gray-500">No additional remarks</span>
                )}
              </div>
            </div>

            {/* Payment Summary */}
            <div
              className="py-2 px-2 rounded-lg"
              style={{ border: '1px solid #e5e7eb' }}
            >
              <h4
                className="text-xs font-semibold text-gray-900 pb-1"
                style={{ borderBottom: '1px solid #d1d5db' }}
              >
                Payment Summary
              </h4>
              <div className="space-y-1">
                <div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-gray-900">
                      Total Amount:
                    </span>
                    <span className="text-xs font-bold text-gray-900">
                      {CURRENCY_SYMBOL}
                      {formatCurrency(data.total_amount)}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-700 font-medium text-xs">
                    Overall Discount:
                  </span>
                  <span className="text-gray-800 font-semibold text-xs">
                    -{CURRENCY_SYMBOL}
                    {formatCurrency(data.overall_discount_amount)}
                  </span>
                </div>

                <div
                  className="flex justify-between items-center pt-3"
                  style={{ borderTop: '1px solid #d1d5db' }}
                >
                  <span className="text-gray-900 font-bold text-sm ">
                    Bill Amount:
                  </span>
                  <span className="text-gray-900 font-bold text-sm">
                    {CURRENCY_SYMBOL}
                    {formatCurrency(data.bill_amount)}
                  </span>
                </div>

                <div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-bold text-gray-900">
                      Due Amount:
                    </span>
                    <span className="text-xs font-bold text-gray-900">
                      {CURRENCY_SYMBOL}
                      {formatCurrency(data.due_amount)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div
            className="mt-4 pt-1 text-center"
            style={{ borderTop: '1px solid #e5e7eb' }}
          >
            <p className="text-gray-600 text-sm mb-2">
              Thank you for choosing Cresence Skin for your skin care needs
            </p>
            <p className="text-gray-500 text-xs">
              For any queries, please contact us at +91 74900 20099
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

InvoicePrintComponent.displayName = 'InvoicePrintComponent';
